/**
 * Punkt 124 (Antoine, 26.09.2026) — die EINE Seite „Auswertung“ als reine Ableitung. Sie ersetzt
 * die frühere Auswertung (P7, Entwürfe 25a/30a–c) und Business Health (AH-7). Freigegebenes
 * Mockup: maestro-uebergabe/mockup-analyse-zusammengefuehrt.html, Abschnitt „So wird gezählt“.
 *
 * Bauart wie bisher: **rein** (keine Datenbank, kein Netz, kein Modell, kein `new Date()`),
 * Tatsachen rein, Kennzahlen raus. Die Datenbank-Seite steht in `apps/api/src/lib/kennzahlen.ts`,
 * und dort — nicht hier — wird „gewonnen“ über `leiteGewinnAb` (lib/gewinn.ts) entschieden.
 *
 * ── Eine Definition je Kennzahl (verbindlich, Antoine 26.09.2026) ────────────────────────────
 *  • Anfrage       — Vorgang mit Anfrage-Zeile, Eingang im Zeitraum, ohne Test/Probelauf/Checkout.
 *  • Auftrag       — gewonnen (leiteGewinnAb) UND nicht storniert. Stornos als eigene Zahl.
 *  • Buchungsquote — Aufträge aus Anfragen ÷ Anfragen desselben Zeitraums, mit Wilson-Bereich.
 *  • Zugesagt      — Auftragsbetrag aller Aufträge (alle Wege), am Annahmetag (Standard) oder am
 *                    Veranstaltungstag (Umschalter).
 *  • Auf dem Konto — erfolgreiche Zahlungen netto nach Erstattung, am Zahltag.
 *  • Ø Auftrag     — Zugesagt ÷ Zahl DERSELBEN Aufträge (Zähler und Nenner gleich geschnitten).
 *  • Antwortzeit   — Eingang bis zur ersten Nachricht oder zum ersten Angebot, was früher kam.
 *  • Vorperiode    — gleich langer Zeitraum direkt davor; „vs. Vorjahr“ derselbe Zeitraum ein Jahr früher.
 *
 * Beträge kommen als Cent-Ganzzahlen herein und werden nur summiert — nie aus einem Modell.
 */
import { ZEITZONE_STANDARD } from "@maestro/anfrage-stand";

export { ZEITZONE_STANDARD };

/* ══════════════════════════════ Wilson: wie sicher ist eine Quote ═════════════════════════ */

/**
 * Der z-Wert eines zweiseitigen 95-%-Intervalls.
 *
 * **Beleg statt Behauptung** (Hausregel „Annahmen über Grenzwerte sind belegpflichtig“):
 * 1,959963985 ist das 0,975-Quantil der Standardnormalverteilung. 95 % ist die übliche
 * Berichtsschwelle; sie steht hier nicht als versteckte Konstante, sondern wird in jedem Satz,
 * der ein Intervall nennt, mitgesagt („belastbarer Bereich, 95 %“).
 */
export const WILSON_Z = 1.959963985;

export interface Intervall {
  /** Beobachteter Anteil (0…1). */
  anteil: number;
  /** Untere und obere Grenze des Wilson-Intervalls (0…1). */
  unten: number;
  oben: number;
}

/**
 * Das **Wilson-Score-Intervall** für `treffer` von `gesamt`.
 *
 * Warum Wilson und nicht die Lehrbuch-Normalapproximation (`p ± z·√(p(1−p)/n)`): letztere ist
 * genau dort unbrauchbar, wo dieser Betrieb steht — bei kleinen `n` und Anteilen nahe 0 oder 1.
 * Sie liefert dann Grenzen unter 0 bzw. über 100 % und ist bei `p = 0` sogar ein Punkt („die
 * Quote ist mit Sicherheit null“). Wilson bleibt in [0, 1] und behält auch bei 0 von 5 eine
 * ehrliche Breite. Die Formel ist geschlossen, ohne Näherung, ohne Tabelle.
 *
 * `null` bei `gesamt <= 0`: über nichts lässt sich nichts sagen — auch nicht „0 %“.
 */
export function wilson(treffer: number, gesamt: number, z: number = WILSON_Z): Intervall | null {
  if (!Number.isFinite(treffer) || !Number.isFinite(gesamt) || gesamt <= 0) return null;
  const n = gesamt;
  const p = Math.min(Math.max(treffer / n, 0), 1);
  const z2 = z * z;
  const nenner = 1 + z2 / n;
  const mitte = (p + z2 / (2 * n)) / nenner;
  const spanne = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / nenner;
  return {
    anteil: p,
    unten: Math.max(0, mitte - spanne),
    oben: Math.min(1, mitte + spanne),
  };
}

/**
 * Überschneiden sich zwei belastbare Bereiche NICHT? Dann gilt der Unterschied als belegt.
 *
 * Das ist bewusst der **konservative** Test: nicht überlappende 95-%-Intervalle implizieren einen
 * Unterschied, aber überlappende schließen ihn nicht aus. Der Fehler geht also immer in dieselbe
 * Richtung — im Zweifel „nicht belastbar“. Für eine Seite, die niemanden zu einer Entscheidung
 * verleiten darf, die die Daten nicht tragen, ist genau das die richtige Richtung.
 */
export function getrennt(a: Intervall | null, b: Intervall | null): boolean {
  if (!a || !b) return false;
  return a.oben < b.unten || b.oben < a.unten;
}

/**
 * Wie viele Fälle je Gruppe bräuchte es, damit sich zwei Bereiche trennen — **bei genau diesem
 * Verhältnis**?
 *
 * Das ist keine Prognose, sondern Arithmetik auf den heutigen Zahlen: die beobachteten Anteile
 * werden festgehalten und die Fallzahl hochgezählt, bis die Intervalle auseinanderrücken. Der
 * Satz dazu sagt das ausdrücklich („bei gleichbleibendem Verhältnis“). Wann diese Fälle da sein
 * werden, behauptet diese Datei an keiner Stelle.
 *
 * `null`, wenn die Anteile gleich sind (dann trennen sie sich nie) oder die Obergrenze reißt.
 */
export function noetigeFallzahl(
  anteilA: number,
  anteilB: number,
  hoechstens = 10_000,
): number | null {
  if (!Number.isFinite(anteilA) || !Number.isFinite(anteilB) || anteilA === anteilB) return null;
  for (let n = 2; n <= hoechstens; n++) {
    const a = wilson(Math.round(anteilA * n), n);
    const b = wilson(Math.round(anteilB * n), n);
    if (getrennt(a, b)) return n;
  }
  return null;
}

/* ══════════════════════════════ Darstellung von Zahlen ════════════════════════════════════ */

/** „42.680 €“ — Cent-Ganzzahl in Euro. Nachkommastellen nur, wenn es welche gibt. */
export function euro(cents: number): string {
  const wert = cents / 100;
  const stellen = cents % 100 === 0 ? 0 : 2;
  return `${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  }).format(wert)} €`;
}

/** „38 %“ — auf ganze Prozent gerundet. */
export function prozent(anteil: number): string {
  return `${Math.round(anteil * 100)} %`;
}

/** „14,6 Std.“ — eine Nachkommastelle, deutsches Komma. */
export function stunden(wert: number): string {
  return `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(wert)} Std.`;
}

/** „08.08.2026“ in der fachlichen Zone. */
export function datum(zeitpunkt: Date, zeitzone: string = ZEITZONE_STANDARD): string {
  try {
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: zeitzone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(zeitpunkt);
  } catch {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(zeitpunkt);
  }
}

const MONATSNAMEN = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** „Juli 2026“ aus „2026-07“. Unlesbares bleibt unverändert stehen statt geraten zu werden. */
export function monatName(monat: string): string {
  const treffer = /^(\d{4})-(\d{2})$/.exec(monat);
  if (!treffer) return monat;
  const nummer = Number(treffer[2]);
  if (nummer < 1 || nummer > 12) return monat;
  return `${MONATSNAMEN[nummer - 1]} ${treffer[1]}`;
}

const ORDNUNGSZAHL: Record<number, string> = {
  2: "zweite", 3: "dritte", 4: "vierte", 5: "fünfte",
  6: "sechste", 7: "siebte", 8: "achte", 9: "neunte", 10: "zehnte",
};

/**
 * „etwa jede dritte“ — die Umgangsform eines Anteils.
 *
 * „etwa“ ist hier **definiert**: der nächstgelegene Stammbruch (1/n mit ganzzahligem n). Das ist
 * keine geratene Toleranz, sondern genau das, was `Math.round(1/anteil)` liefert. Jenseits von
 * 1/10 gibt es keine Umgangsform mehr, die noch etwas erklärt — dann steht nur die Zahl da.
 * Die exakten Werte („13 von 34, 38 %“) stehen ohnehin im selben Satz.
 */
export function etwaJede(anteil: number): string | null {
  if (!Number.isFinite(anteil) || anteil <= 0 || anteil > 1) return null;
  const n = Math.round(1 / anteil);
  const wort = ORDNUNGSZAHL[n];
  return wort ? `etwa jede ${wort}` : null;
}


/* ══════════════════════════════ Kanäle ════════════════════════════════════════════════════ */

/**
 * Beschriftungen der Anfrage-Kanäle (`inquiries.source`). Sie stehen HIER und nicht in der
 * Oberfläche, weil die Auswertung sie in ihre Sätze einbaut — und `apps/web` sie ebenfalls
 * braucht (`ui/format.ts` liest sie von hier). Zwei Listen mit denselben Beschriftungen laufen
 * auseinander; das ist im Repo bereits bei `@maestro/anfrage-stand` die Begründung gewesen.
 */
export const KANAL_TITEL: Record<string, string> = {
  manual: "Manuell angelegt",
  form: "Website-Formular",
  chat: "KI-Chat",
  email: "E-Mail",
  phone: "Telefon",
  // Migration 341 (Posteingang-als-Anfrage): erster Schreibweg, der 'whatsapp' als inquiries.source
  // setzt - der Wert stand seit Migration 133 im Check, hatte aber nie eine Beschriftung.
  whatsapp: "WhatsApp",
};

/** Beschriftung eines Kanals — Unbekanntes wird durchgereicht, nie erfunden. */
export function kanalTitel(quelle: string | null | undefined): string {
  if (!quelle || quelle.trim() === "") return "Ohne Kanal";
  return KANAL_TITEL[quelle] ?? quelle;
}

/* ══════════════════════════════ Herkunft einer Anfrage (Punkt 121) ════════════════════════ */

/**
 * Punkt 121 (Antoine, 25.09.2026): „bei jeder Anfrage sollte stehen, über welches Formular sie
 * gekommen ist“. EINE Regel für Liste, Detailkarte, Agent-Werkzeug und Statistik:
 *
 *  Kanal   = `inquiries.source` mit Titel aus `KANAL_TITEL`.
 *  Eingang = Titel aus `anfrage_eingaenge` (pflegbar je Mandant) → Widget-Name aus
 *            `details.widgetName` → der rohe Slug aus `source_detail` → keiner.
 *
 * Ein Widget-Slug (`widget` bis 25.09.2026, seitdem `widget-<id>`) ohne gepflegten Titel und ohne
 * Widget-Namen heißt „Formular-Widget“ statt einer UUID.
 */
export interface HerkunftFakten {
  source: string | null | undefined;
  sourceDetail?: string | null;
  widgetName?: string | null;
  /** `anfrage_eingaenge.titel` zum `source_detail`, falls gepflegt. */
  eingangTitel?: string | null;
}

export interface Herkunft {
  kanal: string | null;
  kanalTitel: string;
  /** Der konkrete Eingang (Formular), `null` ohne `source_detail`/Widget. */
  eingang: string | null;
  /** Die eine Zeile für die Anzeige: Eingang, sonst Kanal. */
  text: string;
  /** Die Seite, von der die Anfrage kam (nur `http(s)`), `null`, wenn unbekannt. Nur der Server setzt sie. */
  seiteUrl?: string | null;
}

/**
 * Die verlinkbare Seite einer Anfrage: bevorzugt `inquiries.details.originalPage`, sonst die
 * Domain des Eingangs (`anfrage_eingaenge.website`, nur Domain → `https://`). Nur `http(s)` wird
 * zugelassen (kein `javascript:`/`data:`), sonst `null`.
 */
export function seitenUrlAbleiten(originalPage?: string | null, eingangWebsite?: string | null): string | null {
  const pruefe = (roh: string | null | undefined, ohneSchema: boolean): string | null => {
    let t = roh?.trim();
    if (!t || /[\s<>"]/.test(t)) return null;
    if (ohneSchema && !/^[a-z][a-z0-9+.-]*:/i.test(t)) t = `https://${t.replace(/^\/+/, "")}`;
    const m = /^https?:\/\/([^/?#\s]+)([^\s]*)$/i.exec(t);
    if (!m || !/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?(:\d{1,5})?$/i.test(m[1]!)) return null;
    return m[2] ? t : `${t}/`;
  };
  return pruefe(originalPage, false) ?? pruefe(eingangWebsite, true);
}

function getrimmt(wert: string | null | undefined): string | null {
  const t = wert?.trim();
  return t ? t : null;
}

export function herkunftAbleiten(f: HerkunftFakten): Herkunft {
  const kanal = getrimmt(f.source);
  const slug = getrimmt(f.sourceDetail);
  const widget = getrimmt(f.widgetName);
  const eingang =
    getrimmt(f.eingangTitel) ??
    (widget ? `Formular „${widget}“` : null) ??
    (slug && /^widget(-|$)/.test(slug) ? "Formular-Widget" : slug);
  const titel = kanalTitel(kanal);
  return { kanal, kanalTitel: titel, eingang, text: eingang ?? titel };
}

/* ══════════════════════════════ Worüber kommen deine Anfragen? (Punkt 121, Stufe 2) ══════ */

/** Die Zeiträume der Frage, in Tagen. */
export const EINGANG_ZEITRAEUME = [30, 90, 365] as const;
export type EingangZeitraum = (typeof EINGANG_ZEITRAEUME)[number];

/**
 * Eine Gruppe, wie die Datenbank sie zählt (je `source` × `source_detail` × Widget-Name).
 * Definitionen (Punkt 121): Angebot = `events.offer_sent_at` gesetzt; gebucht = Status
 * `offer_chosen`/`paid`/`completed` oder `won_at`; verloren = `cancelled`/`offer_declined`.
 */
export interface EingangsGruppe extends HerkunftFakten {
  anzahl: number;
  angebote: number;
  gebucht: number;
  verloren: number;
  /** Stufe 3: gezählte Formular-Aufrufe im Zeitraum (sql/523); fehlt = 0. */
  aufrufe?: number;
  /**
   * Stufe 4, Antwortzeiten: je beantworteter Anfrage die Stunden von `inquiries.created_at` bis
   * zur ersten Antwort (`least(events.first_response_at, events.offer_sent_at)`); Antworten vor
   * dem Eingang sind nicht dabei (Widerspruch, siehe `@maestro/antwortzeit`). Fehlt = keine.
   */
  antwortStunden?: readonly (number | string)[];
  /** Anfragen ohne Antwort, die älter als 24 Stunden sind — zählen als „nicht binnen 24 h“. */
  ohneAntwortUeber24h?: number;
}

/**
 * Antwortzeit eines Eingangs (oder aller): Median über die beantworteten Anfragen und der Anteil
 * binnen 24 Stunden. Basis des Anteils = beantwortete + seit über 24 h unbeantwortete Anfragen;
 * eine unbeantwortete Anfrage, die jünger als 24 h ist, ist noch offen und zählt nicht.
 */
export interface EingangsAntwortzeit {
  /** Beantwortete Anfragen (Basis des Medians). */
  beantwortet: number;
  /** Median in ganzen Minuten; `null` ohne beantwortete Anfrage. */
  medianMinuten: number | null;
  binnen24h: number;
  basis24h: number;
  /** binnen24h / basis24h. */
  anteilBinnen24h: number;
}

/**
 * Median als GANZE Zahl mit Einheit (unter 2 Stunden in Minuten, sonst in Stunden) — eine
 * Dezimalzahl zerfiele in der Beleg-Ratsche der KI-Analyse in zwei Zählwerte.
 */
export function antwortDauer(minuten: number): { zahl: number; text: string } {
  const zahl = minuten < 120 ? minuten : Math.round(minuten / 60);
  return { zahl, text: `${zahl} ${minuten < 120 ? "Min." : "Std."}` };
}

function median(werte: readonly number[]): number {
  const s = [...werte].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** `null`, wenn nichts zu messen ist (keine Antwort, keine überfällige Anfrage). */
export function leiteAntwortzeitAb(stunden: readonly number[], ohneAntwortUeber24h: number): EingangsAntwortzeit | null {
  const basis24h = stunden.length + ohneAntwortUeber24h;
  if (basis24h === 0) return null;
  const binnen24h = stunden.filter((h) => h <= 24).length;
  return {
    beantwortet: stunden.length,
    medianMinuten: stunden.length ? Math.round(median(stunden) * 60) : null,
    binnen24h,
    basis24h,
    anteilBinnen24h: binnen24h / basis24h,
  };
}

export interface EingangsZeile {
  titel: string;
  kanalTitel: string;
  anzahl: number;
  angebote: number;
  gebucht: number;
  verloren: number;
  angebotsquote: Intervall | null;
  buchungsquote: Intervall | null;
  /** Stufe 3: Formular-Aufrufe im Zeitraum; 0 = noch keine Messung. */
  aufrufe: number;
  /** Anfragen je 100 Aufrufe, eine Nachkommastelle; `null` ohne Aufrufe. */
  anfragenJe100Aufrufe: number | null;
  /** Anfrage → erste Antwort/erstes Angebot; `null` = nichts zu messen. Fehlt in Analysen vor Stufe 4. */
  antwortzeit?: EingangsAntwortzeit | null;
}

export interface EingangsStatistik {
  tage: number;
  frage: string;
  satz: string;
  zeilen: readonly EingangsZeile[];
  gesamt: number;
  /** Getrennt daneben, KEINE Leads (Entscheidung Antoine): nur die Anzahl im Zeitraum. */
  checkoutBestellungen: number;
  vormerkungen: number;
  hinweis: string;
  /** Antwortzeit über alle Eingänge; fehlt in Analysen vor Stufe 4. */
  antwortzeit?: EingangsAntwortzeit | null;
}

/** Beschreibend, keine Bewertung: Zahlen und Bereiche, kein „besser“, keine Empfehlung. */
export function leiteEingangsStatistikAb(
  tage: number,
  gruppen: readonly EingangsGruppe[],
  nebenher: { checkoutBestellungen: number; vormerkungen: number },
  /** Punkt 124: freier Zeitraum — „Vom 1. bis 26. September“ statt „In den letzten N Tagen“. */
  zeitraumText = `In den letzten ${tage} Tagen`,
): EingangsStatistik {
  const jeTitel = new Map<string, EingangsZeile>();
  const antworten = new Map<string, { stunden: number[]; offen: number }>();
  const alle = { stunden: [] as number[], offen: 0 };
  for (const g of gruppen) {
    const h = herkunftAbleiten(g);
    const z = jeTitel.get(h.text) ?? {
      titel: h.text, kanalTitel: h.kanalTitel, anzahl: 0, angebote: 0, gebucht: 0, verloren: 0,
      angebotsquote: null, buchungsquote: null, aufrufe: 0, anfragenJe100Aufrufe: null,
    };
    z.anzahl += g.anzahl;
    z.aufrufe += g.aufrufe ?? 0;
    z.angebote += g.angebote;
    z.gebucht += g.gebucht;
    z.verloren += g.verloren;
    jeTitel.set(h.text, z);
    const a = antworten.get(h.text) ?? { stunden: [], offen: 0 };
    const stunden = (g.antwortStunden ?? []).map(Number);
    a.stunden.push(...stunden);
    a.offen += g.ohneAntwortUeber24h ?? 0;
    alle.stunden.push(...stunden);
    alle.offen += g.ohneAntwortUeber24h ?? 0;
    antworten.set(h.text, a);
  }
  const zeilen = [...jeTitel.values()]
    .map((z) => ({
      ...z,
      angebotsquote: wilson(z.angebote, z.anzahl),
      buchungsquote: wilson(z.gebucht, z.anzahl),
      anfragenJe100Aufrufe: z.aufrufe > 0 ? Math.round((z.anzahl / z.aufrufe) * 1_000) / 10 : null,
      antwortzeit: leiteAntwortzeitAb(antworten.get(z.titel)!.stunden, antworten.get(z.titel)!.offen),
    }))
    .sort((a, b) => b.anzahl - a.anzahl || b.aufrufe - a.aufrufe || a.titel.localeCompare(b.titel, "de"));
  const gesamt = zeilen.reduce((s, z) => s + z.anzahl, 0);
  const mitAnfragen = zeilen.filter((z) => z.anzahl > 0).length;
  const satz =
    gesamt === 0
      ? `${zeitraumText} ist keine Anfrage eingegangen.`
      : `${zeitraumText} kamen ${gesamt} ${gesamt === 1 ? "Anfrage" : "Anfragen"} über ${mitAnfragen} ${mitAnfragen === 1 ? "Eingang" : "Eingänge"}.`;
  return {
    tage,
    frage: "Worüber kommen deine Anfragen?",
    satz,
    zeilen,
    gesamt,
    checkoutBestellungen: nebenher.checkoutBestellungen,
    vormerkungen: nebenher.vormerkungen,
    antwortzeit: leiteAntwortzeitAb(alle.stunden, alle.offen),
    hinweis:
      "Als Testanfrage gekennzeichnete Anfragen zählen nicht mit. Angebot heißt: Angebot verschickt. Gebucht heißt: Angebot angenommen, Kostenübernahme unterschrieben, voll bezahlt oder als gewonnen markiert, ohne stornierte Aufträge. In Klammern der belastbare Bereich (95 %). Aufrufe zählt MAESTRO, wenn ein Formular angezeigt wird — ohne Cookie, ohne Besucherkennung. Antwortzeit: vom Eingang bis zur ersten Antwort oder zum ersten Angebot aus MAESTRO; unbeantwortete Anfragen zählen ab 24 Stunden als nicht rechtzeitig beantwortet.",
  };
}


/* ══════════════════════════════ Punkt 124: Zeitraum ═══════════════════════════════════════ */

/** Ein Zeitraum aus ganzen Berliner Kalendertagen, beide Grenzen eingeschlossen („YYYY-MM-DD“). */
export interface Zeitraum {
  von: string;
  bis: string;
}

export type VergleichArt = "vorperiode" | "vorjahr";
export type UmsatzNach = "annahme" | "veranstaltung";

/** Standard-Zeitraum der Seite (Antoine, 26.09.2026): 90 Tage. */
export const STANDARD_TAGE = 90;
/** Obergrenze eines frei gewählten Zeitraums — zwei Jahre reichen für jeden Saisonvergleich. */
export const MAX_TAGE = 731;

const TAG_MS = 86_400_000;
const TAG_MUSTER = /^\d{4}-\d{2}-\d{2}$/;

/** Kalenderarithmetik auf „YYYY-MM-DD“ — über UTC-Mittag, damit keine Zeitzone hineinspielt. */
export function tagPlus(tag: string, tage: number): string {
  const d = new Date(`${tag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

function jahrMinus(tag: string, jahre: number): string {
  const [j, m, t] = tag.split("-").map(Number) as [number, number, number];
  // 29. Februar → 28. Februar im Nicht-Schaltjahr.
  const letzter = new Date(Date.UTC(j - jahre, m, 0)).getUTCDate();
  return `${j - jahre}-${String(m).padStart(2, "0")}-${String(Math.min(t, letzter)).padStart(2, "0")}`;
}

export function tageImZeitraum(z: Zeitraum): number {
  return Math.round((Date.parse(`${z.bis}T12:00:00Z`) - Date.parse(`${z.von}T12:00:00Z`)) / TAG_MS) + 1;
}

export function istGueltigerTag(tag: unknown): tag is string {
  return typeof tag === "string" && TAG_MUSTER.test(tag) && !Number.isNaN(Date.parse(`${tag}T12:00:00Z`)) && tagPlus(tag, 0) === tag;
}

const TAG_FORMAT = new Map<string, Intl.DateTimeFormat>();
const TAG_CACHE = new WeakMap<Date, string>();

/**
 * Berliner Kalendertag eines Zeitpunkts („YYYY-MM-DD“). Formatter und Ergebnis je Date-Objekt
 * werden gemerkt: die Seite rechnet je Sieben-Tage-Block alle Vorgänge durch (bei 12 Monaten
 * 53 Blöcke) — ein neuer Intl-Formatter je Aufruf kostete auf großen Mandanten Sekunden.
 */
export function berlinTag(zeitpunkt: Date, zeitzone: string = ZEITZONE_STANDARD): string {
  const standard = zeitzone === ZEITZONE_STANDARD;
  const gemerkt = standard ? TAG_CACHE.get(zeitpunkt) : undefined;
  if (gemerkt !== undefined) return gemerkt;
  let f = TAG_FORMAT.get(zeitzone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", { timeZone: zeitzone, year: "numeric", month: "2-digit", day: "2-digit" });
    TAG_FORMAT.set(zeitzone, f);
  }
  const tag = f.format(zeitpunkt);
  if (standard) TAG_CACHE.set(zeitpunkt, tag);
  return tag;
}

/** Ein laufender Kalendermonat („Monat“: vom 1. bis zu einem Tag desselben Monats)? */
export function istMonatsAnfang(z: Zeitraum): boolean {
  return z.von.endsWith("-01") && z.von.slice(0, 7) === z.bis.slice(0, 7);
}

/**
 * Vorperiode: gleich lang, direkt davor. Vorjahr: dieselben Tage ein Jahr früher.
 * Sonderfall „Monat“ (1. bis heute): dieselben Tage des Vormonats — 1.–26. September gegen
 * 1.–26. August, wie im Mockup; „die 26 Tage davor“ wären 6.–31. August.
 */
export function vergleichsZeitraum(z: Zeitraum, art: VergleichArt): Zeitraum {
  if (art === "vorjahr") return { von: jahrMinus(z.von, 1), bis: jahrMinus(z.bis, 1) };
  if (istMonatsAnfang(z)) {
    const von = `${tagPlus(z.von, -1).slice(0, 7)}-01`;
    const letzter = tagPlus(z.von, -1);
    const bis = `${von.slice(0, 8)}${z.bis.slice(8)}`;
    return { von, bis: bis > letzter ? letzter : bis };
  }
  const bis = tagPlus(z.von, -1);
  return { von: tagPlus(bis, -(tageImZeitraum(z) - 1)), bis };
}

const MONATE_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const MONATE_LANG = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

/** „28. Juni 2026“ */
export function tagText(tag: string, mitJahr = true): string {
  const [j, m, t] = tag.split("-").map(Number) as [number, number, number];
  return `${t}. ${MONATE_LANG[m - 1]}${mitJahr ? ` ${j}` : ""}`;
}

/** „28. Juni – 26. September 2026“, im selben Monat „1.–26. August 2026“. */
export function zeitraumText(z: Zeitraum): string {
  if (z.von === z.bis) return tagText(z.von);
  if (z.von.slice(0, 7) === z.bis.slice(0, 7)) return `${Number(z.von.slice(8))}.–${tagText(z.bis)}`;
  return `${tagText(z.von, z.von.slice(0, 4) !== z.bis.slice(0, 4))} – ${tagText(z.bis)}`;
}

/* ══════════════════════════════ Punkt 124: Tatsachen ══════════════════════════════════════ */

/**
 * Ein Vorgang, wie die Kennzahlen ihn brauchen. „Gewonnen“ ist schon entschieden
 * (`leiteGewinnAb`, apps/api/src/lib/gewinn.ts) — diese Datei bildet keine zweite Definition.
 */
export interface KennzahlVorgang extends HerkunftFakten {
  /** `coalesce(events.inquiry_received_at, inquiries.created_at, events.created_at)`. */
  eingangAm: Date;
  /** Hat eine Anfrage-Zeile und ist keine Checkout-Bestellung — nur diese zählen als Anfrage. */
  istAnfrage: boolean;
  /** Checkout-Bestellung (`events.source = 'checkout'`) — steht getrennt, zählt aber beim Umsatz. */
  istCheckout: boolean;
  /** Frühestes Gewinn-Signal aus `leiteGewinnAb`; `null` = nicht gewonnen. */
  gewonnenSeit: Date | null;
  /** `events.status = 'cancelled'`. */
  storniert: boolean;
  /** Abgesagt oder abgelehnt, ohne je gewonnen zu sein (`cancelled`/`offer_declined`). */
  abgelehnt: boolean;
  /** Angebot verschickt (`events.offer_sent_at`). */
  angebotAm: Date | null;
  /** `least(events.first_response_at, events.offer_sent_at)`. */
  antwortAm: Date | null;
  /** Auftragsbetrag (`events.amount_total_cents`, sonst die gewählte Variante). */
  betragCents: number | null;
  /** Veranstaltungstag „YYYY-MM-DD“. */
  veranstaltungAm: string | null;
  /**
   * Migration 731 (Antoine, 19.10.2026): „À la carte“ - Abrechnung vor Ort. Der Vorgang zählt als
   * Auftrag/Annahme, sein Betrag (nur ein etwaiger Zubuchungsbetrag) zählt als Umsatz, aber er geht
   * NICHT in den Durchschnittswert (`schnittCents`) ein. Fehlt = false (ältere Tatsachen-Sätze).
   */
  aLaCarte?: boolean;
  /**
   * Buchungsstrecke D5 (sql/583) — alle optional, damit ältere Tatsachen-Sätze gültig bleiben.
   * Angebotsseite vom Gast aufgerufen (`events.offer_view_count > 0`; die Vorschau des Betriebs
   * zählt nicht). `null`/fehlt = nie gemessen (Angebot vor sql/241), NICHT „nicht angesehen“.
   */
  angesehen?: boolean | null;
  /** A2-Antwort des Gastes (`events.nachfass_antwort`, sql/571). */
  nachfassAntwort?: NachfassAntwort | null;
  /** Jüngster notierter Grund einer Ablehnung/Absage (activity_log `details.grund`). */
  verlustGrund?: string | null;
  /** Wer ihn notiert hat: der Gast (Angebotsseite) oder der Betrieb (Drawer „Angebot abgelehnt“). */
  verlustGrundVon?: "gast" | "betrieb" | null;
  /** `events.status = 'no_response'` (Angebots-Verfall ohne Antwort, sql/149). */
  keineRueckmeldung?: boolean;
  /** B4 (sql/603): Begrüßungsvideo auf der offenen Angebotsseite mindestens einmal abgespielt. */
  videoAbgespielt?: boolean;
}

/** Die vier Antworten der Nachfass-Mail Stufe 3 (A2, sql/571). */
export type NachfassAntwort = "buchen" | "bedenkzeit" | "anders_entschieden" | "budget";

/** Erfolgreiche Zahlungen netto je Berliner Zahltag. */
export interface KennzahlZahlung {
  tag: string;
  nettoCents: number;
}

export interface Kennzahlen {
  anfragen: number;
  /** Anfragen mit verschicktem Angebot (oder schon gewonnen, auch ohne Versand). */
  angebote: number;
  /** Aufträge aus Anfragen: gewonnen und nicht storniert. */
  auftraege: number;
  /** Gewonnene, danach stornierte Anfragen — eigene Zahl, kein Auftrag. */
  storniert: number;
  verloren: number;
  offen: number;
  buchungsquote: Intervall | null;
  /** Zugesagt: alle Aufträge (Anfrage, Checkout, direkt), ohne storniert. */
  zugesagtCents: number;
  /** Zahl DERSELBEN Aufträge wie in `zugesagtCents` (Nenner des Durchschnitts). */
  auftraegeAlle: number;
  /**
   * Durchschnittlicher Auftragswert: OHNE „À la carte“-Aufträge (Migration 731) - ein Auftrag ohne
   * festen Betrag zöge den Schnitt sonst gegen null. `null`, wenn kein bepreister Auftrag da ist.
   */
  schnittCents: number | null;
  /** Aufträge dieses Zeitraums, die „À la carte“ abgerechnet werden (in `auftraegeAlle` enthalten, nicht im Schnitt). */
  aLaCarteAuftraege: number;
  eingegangenCents: number;
  antwortzeit: EingangsAntwortzeit | null;
  checkout: number;
}

function imZeitraum(tag: string | null, z: Zeitraum): boolean {
  return tag !== null && tag >= z.von && tag <= z.bis;
}

function istAuftrag(v: KennzahlVorgang): boolean {
  return v.gewonnenSeit !== null && !v.storniert;
}

/** Stunden bis zur ersten Antwort; `null` ohne Antwort, Antwort vor Eingang ist ein Widerspruch. */
function antwortStunden(v: KennzahlVorgang): number | null {
  if (!v.antwortAm || v.antwortAm.getTime() < v.eingangAm.getTime()) return null;
  return (v.antwortAm.getTime() - v.eingangAm.getTime()) / 3_600_000;
}

function antwortzeitVon(anfragen: readonly KennzahlVorgang[], jetzt: Date): EingangsAntwortzeit | null {
  const stunden: number[] = [];
  let offenUeber24h = 0;
  for (const v of anfragen) {
    const h = antwortStunden(v);
    if (h !== null) stunden.push(h);
    else if (!v.antwortAm && jetzt.getTime() - v.eingangAm.getTime() > 24 * 3_600_000) offenUeber24h++;
  }
  return leiteAntwortzeitAb(stunden, offenUeber24h);
}

/** Die Anfragen eines Zeitraums — gemeinsame Grundmenge aller Anfrage-Kennzahlen. */
export function anfragenIm(vorgaenge: readonly KennzahlVorgang[], z: Zeitraum): KennzahlVorgang[] {
  return vorgaenge.filter((v) => v.istAnfrage && imZeitraum(berlinTag(v.eingangAm), z));
}

/**
 * Die Kennzahlen EINES Zeitraums — die einzige Rechenstelle für Seite, Übersicht und Assistent.
 */
export function leiteKennzahlenAb(
  vorgaenge: readonly KennzahlVorgang[],
  zahlungen: readonly KennzahlZahlung[],
  z: Zeitraum,
  umsatzNach: UmsatzNach,
  jetzt: Date,
): Kennzahlen {
  const anfragen = anfragenIm(vorgaenge, z);
  const auftraege = anfragen.filter(istAuftrag).length;
  const storniert = anfragen.filter((v) => v.gewonnenSeit !== null && v.storniert).length;
  const verloren = anfragen.filter((v) => v.gewonnenSeit === null && v.abgelehnt).length;

  const umsatzAuftraege = vorgaenge.filter(
    (v) =>
      istAuftrag(v) &&
      imZeitraum(umsatzNach === "annahme" ? berlinTag(v.gewonnenSeit!) : v.veranstaltungAm, z),
  );
  const zugesagtCents = umsatzAuftraege.reduce((s, v) => s + (v.betragCents ?? 0), 0);
  // Migration 731: der Durchschnitt rechnet nur über bepreiste Aufträge (Zähler UND Nenner).
  const schnittBasis = umsatzAuftraege.filter((v) => v.aLaCarte !== true);
  const schnittSumme = schnittBasis.reduce((s, v) => s + (v.betragCents ?? 0), 0);

  return {
    anfragen: anfragen.length,
    angebote: anfragen.filter((v) => v.angebotAm !== null || v.gewonnenSeit !== null).length,
    auftraege,
    storniert,
    verloren,
    offen: anfragen.length - auftraege - storniert - verloren,
    buchungsquote: wilson(auftraege, anfragen.length),
    zugesagtCents,
    auftraegeAlle: umsatzAuftraege.length,
    schnittCents: schnittBasis.length > 0 ? Math.round(schnittSumme / schnittBasis.length) : null,
    aLaCarteAuftraege: umsatzAuftraege.length - schnittBasis.length,
    eingegangenCents: zahlungen.filter((p) => imZeitraum(p.tag, z)).reduce((s, p) => s + p.nettoCents, 0),
    antwortzeit: antwortzeitVon(anfragen, jetzt),
    checkout: vorgaenge.filter((v) => v.istCheckout && imZeitraum(berlinTag(v.eingangAm), z)).length,
  };
}

/* ══════════════════════════════ Punkt 124: die Seite ══════════════════════════════════════ */

export type AntwortKlasse = "binnen_4_stunden" | "binnen_24_stunden" | "nach_einem_tag" | "ohne_antwort";

export const ANTWORT_KLASSEN: readonly { klasse: AntwortKlasse; titel: string }[] = [
  { klasse: "binnen_4_stunden", titel: "Binnen 4 Std." },
  { klasse: "binnen_24_stunden", titel: "4 bis 24 Std." },
  { klasse: "nach_einem_tag", titel: "Nach einem Tag" },
  { klasse: "ohne_antwort", titel: "Ohne Antwort aus MAESTRO" },
];

export interface AntwortKlassenZeile {
  klasse: AntwortKlasse;
  titel: string;
  anzahl: number;
  auftraege: number;
  quote: Intervall | null;
}

export interface VerlaufsPunkt {
  titel: string;
  zeitraum: Zeitraum;
  kennzahlen: Kennzahlen;
  /** Anfragen im gleich liegenden Block der Vergleichsperiode. */
  vorherAnfragen: number;
}

export interface UmsatzPunkt {
  titel: string;
  zugesagtCents: number;
  eingegangenCents: number;
}

export interface OffeneZahlungen {
  anzahl: number;
  summeCents: number;
  ueberfaellig: number;
  /** Älteste Überfälligkeit in Tagen; `null`, wenn nichts überfällig ist. */
  aeltesteTage: number | null;
}

export interface Auswertung {
  zeitraum: Zeitraum & { tage: number; text: string };
  vergleich: Zeitraum & { art: VergleichArt; text: string };
  umsatzNach: UmsatzNach;
  aktuell: Kennzahlen;
  vorher: Kennzahlen;
  /** Sieben-Tage-Blöcke ab `zeitraum.von` (der letzte kann kürzer sein). */
  verlauf: readonly VerlaufsPunkt[];
  /** Je Monat, bei Zeiträumen bis 62 Tagen je Sieben-Tage-Block. */
  umsatzVerlauf: readonly UmsatzPunkt[];
  antwortklassen: readonly AntwortKlassenZeile[];
  /** Belegter Satz zu schnell vs. ohne Antwort — oder warum sich nichts sagen lässt. */
  woranEsLiegt: string | null;
  eingaenge: EingangsStatistik;
  offeneZahlungen: OffeneZahlungen;
  /** Buchungsstrecke D5: Stufen je Monat/Kanal, Verlustgründe, vorher/nachher je Phase. */
  buchungsstrecke: Buchungsstrecke;
}

/** Aufrufe je Formular-Eingang (`formular_aufrufe`, sql/523) im Zeitraum. */
export interface AufrufGruppe extends HerkunftFakten {
  aufrufe: number;
}

export interface AuswertungEingabe {
  jetzt: Date;
  zeitraum: Zeitraum;
  vergleichArt: VergleichArt;
  umsatzNach: UmsatzNach;
  vorgaenge: readonly KennzahlVorgang[];
  zahlungen: readonly KennzahlZahlung[];
  aufrufe: readonly AufrufGruppe[];
  vormerkungen: number;
  offeneZahlungen: OffeneZahlungen;
}

function bloecke(z: Zeitraum): Zeitraum[] {
  const aus: Zeitraum[] = [];
  for (let von = z.von; von <= z.bis; von = tagPlus(von, 7)) {
    const bis = tagPlus(von, 6);
    aus.push({ von, bis: bis > z.bis ? z.bis : bis });
  }
  return aus;
}

function monatsBloecke(z: Zeitraum): { titel: string; zeitraum: Zeitraum }[] {
  const aus: { titel: string; zeitraum: Zeitraum }[] = [];
  let [j, m] = z.von.split("-").map(Number) as [number, number];
  for (;;) {
    const erster = `${j}-${String(m).padStart(2, "0")}-01`;
    if (erster > z.bis) break;
    const letzter = tagPlus(`${m === 12 ? j + 1 : j}-${String(m === 12 ? 1 : m + 1).padStart(2, "0")}-01`, -1);
    aus.push({
      titel: MONATE_KURZ[m - 1]!,
      zeitraum: { von: erster < z.von ? z.von : erster, bis: letzter > z.bis ? z.bis : letzter },
    });
    m++;
    if (m > 12) {
      m = 1;
      j++;
    }
  }
  return aus;
}

function kurzTag(tag: string): string {
  const [, m, t] = tag.split("-").map(Number) as [number, number, number];
  return `${t}.${m}.`;
}

function antwortKlasse(v: KennzahlVorgang): AntwortKlasse | null {
  if (!v.antwortAm) return "ohne_antwort";
  const h = antwortStunden(v);
  if (h === null) return null; // Antwort vor Eingang: nicht messbar
  return h <= 4 ? "binnen_4_stunden" : h <= 24 ? "binnen_24_stunden" : "nach_einem_tag";
}

function woranEsLiegt(zeilen: readonly AntwortKlassenZeile[]): string | null {
  const schnell = zeilen.find((z) => z.klasse === "binnen_4_stunden")!;
  const ohne = zeilen.find((z) => z.klasse === "ohne_antwort")!;
  if (!schnell.quote || !ohne.quote) return null;
  if (!getrennt(schnell.quote, ohne.quote)) {
    return `Binnen 4 Std. beantwortete Anfragen wurden zu ${prozent(schnell.quote.anteil)} gebucht, solche ohne Antwort aus MAESTRO zu ${prozent(ohne.quote.anteil)}. Bei dieser Fallzahl überschneiden sich die belastbaren Bereiche (95 %), ein Unterschied ist noch nicht belegt.`;
  }
  const faktor = ohne.quote.anteil > 0 ? Math.round(schnell.quote.anteil / ohne.quote.anteil) : null;
  const vergleich =
    schnell.quote.anteil > ohne.quote.anteil
      ? faktor && faktor >= 2
        ? `etwa ${faktor}-mal so oft gebucht`
        : "deutlich häufiger gebucht"
      : "seltener gebucht";
  return `Binnen 4 Std. beantwortete Anfragen werden hier ${vergleich} wie solche ohne Antwort aus MAESTRO (${prozent(schnell.quote.anteil)} gegenüber ${prozent(ohne.quote.anteil)}). Bei dieser Fallzahl ist der Unterschied belegt, die belastbaren Bereiche (95 %) überschneiden sich nicht.`;
}

/**
 * Die Eingangs-Statistik eines Zeitraums: DIESELBEN Anfragen, dieselbe Auftrags-Definition wie die
 * Kacheln (gewonnen ohne storniert). Auch der monatliche Lauf der Lead-Analyse rechnet hiermit.
 */
export function leiteEingaengeAb(
  e: Pick<AuswertungEingabe, "zeitraum" | "vorgaenge" | "aufrufe" | "vormerkungen" | "jetzt">,
): EingangsStatistik {
  const z = e.zeitraum;
  const gruppen: EingangsGruppe[] = anfragenIm(e.vorgaenge, z).map((a) => {
    const h = antwortStunden(a);
    return {
      source: a.source,
      sourceDetail: a.sourceDetail,
      widgetName: a.widgetName,
      eingangTitel: a.eingangTitel,
      anzahl: 1,
      angebote: a.angebotAm !== null || a.gewonnenSeit !== null ? 1 : 0,
      gebucht: istAuftrag(a) ? 1 : 0,
      verloren: a.gewonnenSeit === null && a.abgelehnt ? 1 : 0,
      antwortStunden: h === null ? [] : [h],
      ohneAntwortUeber24h: !a.antwortAm && e.jetzt.getTime() - a.eingangAm.getTime() > 24 * 3_600_000 ? 1 : 0,
    };
  });
  const aufrufGruppen: EingangsGruppe[] = e.aufrufe.map((g) => ({ ...g, anzahl: 0, angebote: 0, gebucht: 0, verloren: 0 }));
  const checkout = e.vorgaenge.filter((v) => v.istCheckout && imZeitraum(berlinTag(v.eingangAm), z)).length;
  return leiteEingangsStatistikAb(
    tageImZeitraum(z),
    [...gruppen, ...aufrufGruppen],
    { checkoutBestellungen: checkout, vormerkungen: e.vormerkungen },
    `Vom ${tagText(z.von, z.von.slice(0, 4) !== z.bis.slice(0, 4))} bis ${tagText(z.bis)}`,
  );
}

/* ══════════════════════════════ Buchungsstrecke D5 ════════════════════════════════════════ */

/**
 * Buchungsstrecke D5 (docs/LOOP-BUCHUNGSSTRECKE.md, Konzept § 1 + § 7): Anfrage → Angebot →
 * angesehen → gebucht, je Monat und Kanal, Verlustgründe und vorher/nachher je Phase.
 *
 * Keine neue Definition: Anfrage = `anfragenIm`, Angebot = wie `Kennzahlen.angebote`, gebucht =
 * `istAuftrag` (leiteGewinnAb ohne storniert), Bereiche = `wilson`. Neu ist nur „angesehen“
 * (Aufruf-Zähler der Angebotsseite, sql/241) und die Hauptkennzahl des Konzepts: Anteil der
 * verschickten Angebote, die binnen 21 Tagen gebucht werden.
 */

/** Hauptkennzahl (Konzept § 1): binnen so vieler Tage nach dem Versand gebucht. */
export const BUCHUNG_BINNEN_TAGE = 21;
/** Unter so vielen Angeboten je Seite steht „noch zu wenig Daten“ statt einer Zahl. */
export const MINDEST_ANGEBOTE = 20;
/** Länge des „vorher“-Fensters vor einem Phasen-Stichtag. */
export const VORHER_TAGE = 90;

/**
 * Stichtage der Phasen (Berliner Kalendertag, an dem die Kriterien live gingen). Der Loop trägt
 * spätere Phasen hier nach, sobald sie live sind — eine Phase ohne Eintrag wird nicht verglichen.
 */
export interface Phase {
  id: "A" | "B" | "C" | "D";
  titel: string;
  live: readonly { tag: string; punkte: readonly string[] }[];
}

export const PHASEN: readonly Phase[] = [
  { id: "A", titel: "Phase A: ehrliches Nachfassen, Texte, Ansprechpartner", live: [{ tag: "2026-09-29", punkte: ["S0", "A1", "A2", "A4"] }] },
  { id: "B", titel: "Phase B: Angebotsseite und Mail", live: [] },
  { id: "C", titel: "Phase C: Zusage und danach", live: [] },
  { id: "D", titel: "Phase D: Tempo und Steuerung", live: [] },
];

/** Die Antwortmöglichkeiten des Drawers „Angebot abgelehnt“ (apps/web, event-detail.tsx). */
export const ABLEHNUNGS_GRUENDE = [
  "Zu teuer",
  "Termin anderweitig vergeben",
  "Gast hat woanders gebucht",
  "Veranstaltung abgesagt",
  "Keine Rückmeldung mehr",
] as const;

export interface StreckenStufen {
  anfragen: number;
  /** Angebot verschickt (oder schon gewonnen) — wie `Kennzahlen.angebote`. */
  angebote: number;
  /** Davon mit gemessenem Aufruf-Zähler (Basis von „angesehen“). */
  angeboteGemessen: number;
  /** Vom Gast mindestens einmal geöffnet. */
  angesehen: number;
  /** Aufträge: gewonnen und nicht storniert. */
  gebucht: number;
}

export interface StreckenZeile {
  titel: string;
  stufen: StreckenStufen;
}

export interface Verlustgrund {
  schluessel: string;
  titel: string;
  quelle: "gast_antwort" | "betrieb" | "gast";
  anzahl: number;
}

export interface Verlustgruende {
  /** Nicht gebuchte Anfragen mit einem Verlustsignal (Summe aller Zeilen unten). */
  gesamt: number;
  gruende: readonly Verlustgrund[];
  /** Angebot verfallen ohne Antwort oder vom Betrieb als „Keine Rückmeldung mehr“ notiert. */
  keineRueckmeldung: number;
  /** Abgelehnt oder abgesagt, aber kein Grund notiert. */
  ohneGrund: number;
}

export interface AngebotsQuote {
  zeitraum: Zeitraum;
  /** Verschickte Angebote, deren 21 Tage vorbei sind. */
  basis: number;
  /** Davon binnen 21 Tagen gebucht (und nicht storniert). */
  gebucht: number;
  quote: Intervall | null;
  /** Im Zeitraum verschickt, aber die 21 Tage laufen noch — zählen noch nicht. */
  nochInFrist: number;
  /** basis < MINDEST_ANGEBOTE: keine Zahl zeigen. */
  zuWenig: boolean;
}

export interface PhasenVergleich {
  id: Phase["id"];
  titel: string;
  stichtag: string;
  punkte: readonly string[];
  vorher: AngebotsQuote;
  nachher: AngebotsQuote;
  satz: string;
}

/**
 * B4 (sql/603) — Begrüßungsvideo: wie oft es abgespielt wird und ob Gäste, die es gesehen haben,
 * häufiger buchen. Nur Anfragen im Zeitraum, deren Angebot vom Gast angesehen wurde (sonst konnte
 * er das Video gar nicht sehen). „Mit/ohne“ ist ein Zusammenhang, kein Beweis der Wirkung — wer
 * ohnehin buchen will, schaut eher.
 */
export interface VideoQuote {
  /** Angesehene Angebote (Basis der Abspielquote). */
  angesehen: number;
  /** Davon mit abgespieltem Video. */
  abgespielt: number;
  abspielquote: Intervall | null;
  /** Gebucht unter denen mit Abspielen / ohne Abspielen (nicht: mit/ohne hochgeladenes Video). */
  gebuchtMit: number;
  gebuchtOhne: number;
  buchungMit: Intervall | null;
  buchungOhne: Intervall | null;
}

export interface Buchungsstrecke {
  gesamt: StreckenStufen;
  jeMonat: readonly StreckenZeile[];
  jeKanal: readonly StreckenZeile[];
  /** Hauptkennzahl im gewählten Zeitraum (Angebote nach Versandtag). */
  hauptkennzahl: AngebotsQuote;
  verlust: Verlustgruende;
  phasen: readonly PhasenVergleich[];
  /** Phasen ohne Stichtag (noch nicht live) — nur die Namen. */
  phasenOffen: readonly string[];
  /** B4: `null`, solange im Zeitraum kein Video abgespielt wurde (kein Video oder niemand schaut). */
  video: VideoQuote | null;
}

/** B4 — Abspielquote und Buchungsquote mit/ohne Abspielen des Begrüßungsvideos. */
export function leiteVideoQuoteAb(anfragen: readonly KennzahlVorgang[]): VideoQuote | null {
  const angesehen = anfragen.filter((v) => v.angesehen === true);
  const mit = angesehen.filter((v) => v.videoAbgespielt === true);
  if (mit.length === 0) return null;
  const ohne = angesehen.filter((v) => v.videoAbgespielt !== true);
  const gebuchtMit = mit.filter(istAuftrag).length;
  const gebuchtOhne = ohne.filter(istAuftrag).length;
  return {
    angesehen: angesehen.length,
    abgespielt: mit.length,
    abspielquote: wilson(mit.length, angesehen.length),
    gebuchtMit,
    gebuchtOhne,
    buchungMit: wilson(gebuchtMit, mit.length),
    buchungOhne: wilson(gebuchtOhne, ohne.length),
  };
}

function stufenVon(anfragen: readonly KennzahlVorgang[]): StreckenStufen {
  const angebote = anfragen.filter((v) => v.angebotAm !== null || v.gewonnenSeit !== null);
  const gemessen = angebote.filter((v) => v.angesehen === true || v.angesehen === false);
  return {
    anfragen: anfragen.length,
    angebote: angebote.length,
    angeboteGemessen: gemessen.length,
    angesehen: gemessen.filter((v) => v.angesehen === true).length,
    gebucht: anfragen.filter(istAuftrag).length,
  };
}

const TAG_MS_STRECKE = 86_400_000;

/**
 * Hauptkennzahl: Angebote mit Versandtag im Zeitraum, deren 21 Tage bis `jetzt` vorbei sind;
 * gebucht heißt Auftrag (ohne Storno) mit Gewinn-Zeitpunkt höchstens 21 Tage nach dem Versand.
 * Ohne Anfrage-Bindung: auch direkt angelegte Angebote zählen, Checkout-Bestellungen nicht.
 */
export function leiteAngebotsQuoteAb(vorgaenge: readonly KennzahlVorgang[], z: Zeitraum, jetzt: Date): AngebotsQuote {
  const frist = BUCHUNG_BINNEN_TAGE * TAG_MS_STRECKE;
  const verschickt = vorgaenge.filter((v) => !v.istCheckout && v.angebotAm !== null && imZeitraum(berlinTag(v.angebotAm), z));
  const reif = verschickt.filter((v) => v.angebotAm!.getTime() + frist <= jetzt.getTime());
  const gebucht = reif.filter((v) => istAuftrag(v) && v.gewonnenSeit!.getTime() <= v.angebotAm!.getTime() + frist).length;
  return {
    zeitraum: z,
    basis: reif.length,
    gebucht,
    quote: wilson(gebucht, reif.length),
    nochInFrist: verschickt.length - reif.length,
    zuWenig: reif.length < MINDEST_ANGEBOTE,
  };
}

const A2_TITEL: Record<string, string> = {
  anders_entschieden: "Anders entschieden (Antwort auf Nachfrage)",
  budget: "Budget passt nicht (Antwort auf Nachfrage)",
};

/**
 * Warum nicht gebucht? Genau EIN Grund je Vorgang, nur gezählt, nie vermutet. Reihenfolge:
 *  1. A2-Antwort des Gastes `anders_entschieden`/`budget` (strukturiert, seine eigene Aussage);
 *  2. notierter Grund des Betriebs (Drawer-Auswahl; „Keine Rückmeldung mehr“ → keineRueckmeldung;
 *     freier Text → „Anderer Grund“);
 *  3. Grund des Gastes auf der Angebotsseite (Freitext, darum nur als eine Zeile gezählt);
 *  4. Status „keine Rückmeldung“ (Angebots-Verfall);
 *  5. abgelehnt/abgesagt ohne Grund → ohneGrund.
 * Menge: Anfragen im Zeitraum, nie gewonnen.
 */
export function leiteVerlustgruendeAb(anfragen: readonly KennzahlVorgang[]): Verlustgruende {
  const zaehler = new Map<string, Verlustgrund>();
  let keineRueckmeldung = 0;
  let ohneGrund = 0;
  const plus = (schluessel: string, titel: string, quelle: Verlustgrund["quelle"]) => {
    const g = zaehler.get(schluessel) ?? { schluessel, titel, quelle, anzahl: 0 };
    g.anzahl++;
    zaehler.set(schluessel, g);
  };
  for (const v of anfragen) {
    if (v.gewonnenSeit !== null) continue;
    const a2 = v.nachfassAntwort;
    if (a2 === "anders_entschieden" || a2 === "budget") {
      plus(`a2:${a2}`, A2_TITEL[a2]!, "gast_antwort");
      continue;
    }
    if (!v.abgelehnt && !v.keineRueckmeldung) continue;
    const grund = v.verlustGrund?.trim() || null;
    if (grund && v.verlustGrundVon !== "gast") {
      if (grund === "Keine Rückmeldung mehr") keineRueckmeldung++;
      else if ((ABLEHNUNGS_GRUENDE as readonly string[]).includes(grund)) plus(`betrieb:${grund}`, grund, "betrieb");
      else plus("betrieb:anderer", "Anderer Grund", "betrieb");
      continue;
    }
    if (grund) {
      plus("gast:text", "Vom Gast abgesagt (eigener Text)", "gast");
      continue;
    }
    if (v.keineRueckmeldung) keineRueckmeldung++;
    else ohneGrund++;
  }
  const gruende = [...zaehler.values()].sort((a, b) => b.anzahl - a.anzahl || a.titel.localeCompare(b.titel, "de"));
  return { gesamt: gruende.reduce((s, g) => s + g.anzahl, 0) + keineRueckmeldung + ohneGrund, gruende, keineRueckmeldung, ohneGrund };
}

function phasenSatz(vorher: AngebotsQuote, nachher: AngebotsQuote): string {
  if (vorher.zuWenig || nachher.zuWenig) {
    return `Noch zu wenig Daten: vorher ${vorher.basis}, nachher ${nachher.basis} Angebote mit abgelaufener Frist (ab ${MINDEST_ANGEBOTE} je Seite).`;
  }
  const a = vorher.quote!;
  const b = nachher.quote!;
  const kern = `Vorher ${prozent(a.anteil)} (${bereichText(a)}), nachher ${prozent(b.anteil)} (${bereichText(b)}).`;
  return getrennt(a, b)
    ? `${kern} Die belastbaren Bereiche (95 %) überschneiden sich nicht, der Unterschied ist belegt.`
    : `${kern} Die belastbaren Bereiche (95 %) überschneiden sich, ein Unterschied ist noch nicht belegt.`;
}

function bereichText(q: Intervall): string {
  return `${Math.round(q.unten * 100)}–${Math.round(q.oben * 100)} %`;
}

/** Vorher = 90 Tage vor dem Stichtag; nachher = Stichtag bis zum nächsten Stichtag bzw. heute. */
export function leitePhasenVergleichAb(vorgaenge: readonly KennzahlVorgang[], jetzt: Date, phasen: readonly Phase[] = PHASEN): PhasenVergleich[] {
  const heute = berlinTag(jetzt);
  const mitTag = phasen
    .filter((p) => p.live.length > 0)
    .map((p) => ({ p, stichtag: [...p.live].map((l) => l.tag).sort()[0]! }))
    .sort((a, b) => a.stichtag.localeCompare(b.stichtag));
  return mitTag
    .filter(({ stichtag }) => stichtag <= heute)
    .map(({ p, stichtag }, i) => {
      const naechster = mitTag[i + 1]?.stichtag;
      const bis = naechster && tagPlus(naechster, -1) < heute ? tagPlus(naechster, -1) : heute;
      const vorher = leiteAngebotsQuoteAb(vorgaenge, { von: tagPlus(stichtag, -VORHER_TAGE), bis: tagPlus(stichtag, -1) }, jetzt);
      const nachher = leiteAngebotsQuoteAb(vorgaenge, { von: stichtag, bis }, jetzt);
      return { id: p.id, titel: p.titel, stichtag, punkte: p.live.flatMap((l) => l.punkte), vorher, nachher, satz: phasenSatz(vorher, nachher) };
    });
}

/** Frühester Tag, den die Phasen-Vergleiche brauchen (für die Datenbeschaffung). */
export function fruehesterPhasenTag(phasen: readonly Phase[] = PHASEN): string | null {
  const tage = phasen.flatMap((p) => p.live.map((l) => l.tag)).sort();
  return tage[0] ? tagPlus(tage[0], -VORHER_TAGE) : null;
}

export function leiteBuchungsstreckeAb(vorgaenge: readonly KennzahlVorgang[], z: Zeitraum, jetzt: Date): Buchungsstrecke {
  const anfragen = anfragenIm(vorgaenge, z);
  const jeKanal = new Map<string, KennzahlVorgang[]>();
  for (const a of anfragen) {
    const titel = herkunftAbleiten(a).kanalTitel;
    jeKanal.set(titel, [...(jeKanal.get(titel) ?? []), a]);
  }
  return {
    gesamt: stufenVon(anfragen),
    jeMonat: monatsBloecke(z).map((b) => ({
      titel: `${b.titel} ${b.zeitraum.von.slice(0, 4)}`,
      stufen: stufenVon(anfragen.filter((a) => imZeitraum(berlinTag(a.eingangAm), b.zeitraum))),
    })),
    jeKanal: [...jeKanal.entries()]
      .map(([titel, menge]) => ({ titel, stufen: stufenVon(menge) }))
      .sort((a, b) => b.stufen.anfragen - a.stufen.anfragen || a.titel.localeCompare(b.titel, "de")),
    hauptkennzahl: leiteAngebotsQuoteAb(vorgaenge, z, jetzt),
    verlust: leiteVerlustgruendeAb(anfragen),
    phasen: leitePhasenVergleichAb(vorgaenge, jetzt),
    phasenOffen: PHASEN.filter((p) => p.live.length === 0).map((p) => p.titel),
    video: leiteVideoQuoteAb(anfragen),
  };
}

/** Die ganze Seite aus einem Satz Tatsachen. */
export function leiteAuswertungAb(e: AuswertungEingabe): Auswertung {
  const z = e.zeitraum;
  const v = vergleichsZeitraum(z, e.vergleichArt);
  const tage = tageImZeitraum(z);
  const kz = (r: Zeitraum) => leiteKennzahlenAb(e.vorgaenge, e.zahlungen, r, e.umsatzNach, e.jetzt);

  const vorherBloecke = bloecke(v);
  const verlauf = bloecke(z).map((b, i) => ({
    titel: kurzTag(b.von),
    zeitraum: b,
    kennzahlen: kz(b),
    vorherAnfragen: vorherBloecke[i] ? anfragenIm(e.vorgaenge, vorherBloecke[i]!).length : 0,
  }));
  const umsatzVerlauf = (tage <= 62 ? verlauf.map((p) => ({ titel: p.titel, zeitraum: p.zeitraum })) : monatsBloecke(z)).map((b) => {
    const k = kz(b.zeitraum);
    return { titel: b.titel, zugesagtCents: k.zugesagtCents, eingegangenCents: k.eingegangenCents };
  });

  const anfragen = anfragenIm(e.vorgaenge, z);
  const antwortklassen = ANTWORT_KLASSEN.map(({ klasse, titel }) => {
    const menge = anfragen.filter((a) => antwortKlasse(a) === klasse);
    const auftraege = menge.filter(istAuftrag).length;
    return { klasse, titel, anzahl: menge.length, auftraege, quote: wilson(auftraege, menge.length) };
  });

  const aktuell = kz(z);
  const text = zeitraumText(z);

  return {
    zeitraum: { ...z, tage, text },
    vergleich: {
      ...v,
      art: e.vergleichArt,
      text:
        e.vergleichArt === "vorjahr"
          ? `verglichen mit demselben Zeitraum ${v.bis.slice(0, 4)}`
          : istMonatsAnfang(z)
            ? `verglichen mit ${zeitraumText(v)}`
            : `verglichen mit den ${tage} Tagen davor`,
    },
    umsatzNach: e.umsatzNach,
    aktuell,
    vorher: kz(v),
    verlauf,
    umsatzVerlauf,
    antwortklassen,
    woranEsLiegt: woranEsLiegt(antwortklassen),
    eingaenge: leiteEingaengeAb(e),
    offeneZahlungen: e.offeneZahlungen,
    buchungsstrecke: leiteBuchungsstreckeAb(e.vorgaenge, z, e.jetzt),
  };
}
